import { Group, Stack, Text, Title } from "@mantine/core";
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from "convex/react";
import { useCallback } from "react";
import { writingRoles } from "../../../convex/shared_util";
import { useDetailStack, useOpenDetailId } from '../../components/DetailStack';
import FloatingButton from "../../components/FloatingButton";
import LayoutChooser, { useLayoutState } from "../../components/LayoutChooser";
import { openAddPlantModal } from "../../components/PlantDialog";
import { PlantFilterSettings } from "../../components/PlantFilterDialog";
import { plantItemSelector } from '../../components/PlantItem';
import PlantList from "../../components/PlantList";
import { PlantsFilter, usePlantsFilterState } from "../../components/PlantsFilter";
import { buildSections, IndexSection, nameSectionKey, SectionIndex } from "../../components/SectionIndex";
import useCurrentGarden from "../../hooks/useCurrentGarden";
import { useGardenLocationMap, useGardenLocations, useGardenPlants } from "../../hooks/useGardenData";
import { locationPathLabel } from "../../lib/locationPath";
import { filterAndSortPlants } from "../../lib/plantFilter";
import { waitForElement } from '../../lib/util';

import classes from "./panel.module.css";

/**
 * Below this many plants the list is a screen or two of scrolling, so section headers
 * are noise and the index strip has nothing to save.
 */
const SECTION_MIN_PLANTS = 12;

/**
 * @param count a number of plants
 * @returns the count with "plant" or "plants", as the count line under the filter reads it
 */
function pluralizePlants(count: number): string {
  return `${count} ${count === 1 ? "plant" : "plants"}`;
}

/**
 * Module scope because it is constant, and because an unresolved garden hands it back
 * as the value itself (`usePersistentUIState/SPEC.md` R19) — rebuilding it per render
 * would churn the identity of everything derived from the filter on those renders.
 */
const defaultFilter: PlantFilterSettings = {
  name: "",
  locationId: undefined,
  includeGone: false,
  sortFields: ["location", "name", "ts-earliest", "ts-latest"],
  // locations in tree order and names A→Z, both of which "ascending" now names directly;
  // entry dates newest first, which is the useful end of a journal
  sortDirections: {
    location: "ascending",
    name: "ascending",
    "ts-earliest": "descending",
    "ts-latest": "descending",
  },
};

export const Route = createFileRoute('/_app/g/$gardenToken/plants')({
  component: function PlantsPane() {
  const detailStack = useDetailStack();
  // the plant `?v=` has open, which the table layout highlights
  const openPlantId = useOpenDetailId("plant");
  const { plants } = useGardenPlants();
  const currentGarden = useCurrentGarden();
  // both: the map for lookups, the list because only it distinguishes "no locations" from
  // "not loaded yet", which is what `validatePlantFilterLocation` needs
  const locations = useGardenLocations();
  const locationMap = useGardenLocationMap();

  const convex = useConvex();

  const [layout, setLayout] = useLayoutState("plants-pane-2", "list", ["table", "list", "cards", "grid"]);

  // Persisted per garden, except the name search, which clears when the pane unmounts; a
  // restored location this garden no longer has is dropped — see PlantsFilter/README.md
  const [filter, setFilter] = usePlantsFilterState(defaultFilter, locations);

  const scrollToSection = useCallback((section: IndexSection) => {
    // the anchor is the section wrapper, not the sticky header inside it — see
    // PlantList.module.css for why. Only the name sort renders a strip, and
    // `nameSectionKey` only ever yields a single letter or "#", so the quoted attribute
    // selector needs no escaping
    document
      .querySelector(`[data-section-key="${section.key}"]`)
      ?.scrollIntoView({ block: "start", behavior: "instant" });
  }, []);

  if (!currentGarden) { return <></>; }

  const filteredPlants = plants ? filterAndSortPlants(plants, filter, locationMap) : [];

  // The total follows "Include archived": with it off, archived plants are not part of
  // the garden the user is looking at, so they are not counted either.
  const totalPlants = plants
    ? filter.includeGone
      ? plants.length
      : plants.filter((plant) => !plant.archived).length
    : 0;

  // Only the location and name narrow the list; archived plants change what the total is,
  // and the sort changes nothing. A whitespace-only name matches every plant, so it is not
  // a filter either.
  const filterActive = filter.locationId !== undefined || filter.name.trim() !== "";

  // Sections describe the name and location sorts; the entry-date sorts get none, since
  // their runs are months and a section per month says little the dates do not. Built
  // from `filteredPlants` in its final order, never re-derived — the sections and the
  // list have to agree.
  const primarySort = filteredPlants.length >= SECTION_MIN_PLANTS ? filter.sortFields[0] : undefined;
  const sections =
    primarySort === "name"
      ? buildSections(filteredPlants, (plant) => nameSectionKey(plant.name), (key) => key)
      : primarySort === "location"
        // `filterAndSortPlants` orders by location, so a location's plants are already one
        // adjacent run. Keyed by id rather than name because two locations under different
        // parents may share a name — which is also why the label is the full ancestor path
        // and not the bare name the plant carries.
        ? buildSections(
          filteredPlants,
          (plant) => plant.location?.locationId ?? "",
          (_key, plant) =>
            plant.location
              ? locationPathLabel(plant.location.locationId, locationMap, plant.location.name)
              : "No location"
        )
        : [];
  const sectioned = sections.length >= 2;
  // The strip is for the name sort alone: location labels are multi-word and long, the
  // sections are unbalanced, and the count is unbounded — see SectionIndex/ARCHITECTURE.md
  const indexed = sectioned && primarySort === "name";

  return (
    <div className={classes.panel} data-scroll-restoration-id="plants-panel">
      {writingRoles.includes(currentGarden.role) && <FloatingButton
        label="Add plant"
        onClick={() => {
          openAddPlantModal(
            convex, currentGarden._id,
            undefined,
            (plant) => {
              detailStack.push({ plant });
              waitForElement(plantItemSelector(plant._id), (element) => {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
              });
            });
        }}
      />}
      {/* A fixed overlay, so it sits beside `FloatingButton` as a direct child of the panel
          rather than next to the list it indexes: both read the topbar/tabbar constants for
          their offsets and want them measured against the viewport.

          `indexed` already implies `plants`: with no plants there are no sections. */}
      {indexed && (
        <SectionIndex sections={sections} onSelect={scrollToSection} alwaysVisible={true} decimation="bullet"/>
      )}
      <Stack gap="md">
        {/* the header's structure and spacing match `journal.tsx`: title and filter row in a
            tight stack, then `md` to the list. The count has no counterpart there, so it joins
            the header at the same spacing rather than opening a gap of its own */}
        <Stack gap="xxs">
          <Group mt="md" justify="space-between" align="center">
            <Title order={2}>Plants</Title>
            {plants && plants.length > 0 && (
              <LayoutChooser options={["table", "list", "cards", "grid"]} activeLayout={layout} setLayout={setLayout} />
            )}
          </Group>
          {/* shown with no plants too, so the row does not appear late once they arrive */}
          <PlantsFilter
            locations={locations}
            defaultValue={defaultFilter}
            value={filter}
            onChange={setFilter}
          />
          {plants && plants.length > 0 && (
            <Text c="dimmed" mt="xxs" size="xs">
              {filterActive
                ? `${pluralizePlants(filteredPlants.length)} found`
                : `${pluralizePlants(totalPlants)} total`}
            </Text>
          )}
        </Stack>
        {plants && (<>
          <PlantList
            layout={layout}
            plants={filteredPlants}
            sections={sectioned ? sections : undefined}
            openId={openPlantId}
            onItemClick={(_, index) => detailStack.push({ plants: filteredPlants.map((p) => ({ plant: p })), activeIndex: index })}
          />
          {plants && (plants.length === 0 || filteredPlants.length > 0) && plants.length <= 3 && (
            <Text c="dimmed" size="sm">
              Keep track of your plants in this tab. But you don&rsquo;t have to record <em>every</em> plant in your garden,
              just the ones you want to track individually&hellip;
            </Text>
          )}
          {filteredPlants.length === 0 && plants.length > 0 && (
            <Text c="dimmed" size="sm">
              No plants match your filter!
            </Text>
          )}
        </>
        )}
      </Stack>
    </div>
  );
  },
})
